import { inject, Injectable } from '@angular/core';
import { CoreOsVersion, IcpSensorField, SensorActiveStatus, SensorStatus } from '@uigy/generated/graphql';
import { ColDef, ICellRendererParams, ValueFormatterParams, ValueGetterParams } from '@ag-grid-community/core';
import { LanguageService } from '@uigy/shared/services/language.service';
import { DelimitersEnum } from '@uigy/shared/enums/delimiters.enum';
import { FileSizesPipe } from '@uigy/shared/pipes/file-sizes.pipe';
import { DateTimeService } from '@uigy/shared/services/date-time/date-time.service';
import { DatesFormatsEnum } from '@uigy/shared/enums/dateFormats.enum';
import Utils from '@uigy/utils';
import { SensorsService } from '@uigy/modules/sensors/services/sensors.service';
import { SensorsDataHelperService } from '@uigy/modules/sensors/constant/sensors-data-helper.service';
import {
  StateIndicatorCellRendererComponent,
  StateIndicatorCellRendererParams,
} from '@uigy/shared/modules/grid/cell-renderers/state-indicator-cell-renderer/state-indicator-cell-renderer.component';
import { StateIndicatorType } from '@uigy/shared/components/icons/interfaces';
import { GridFormatterService } from '@uigy/shared/modules/grid/formatters/grid-formatter.service';
import { TimeFilterSelectorComponent } from '@uigy/shared/modules/grid/filters/time-filter/time-filter-selector.component';
import { createFilterComponentMap } from '@uigy/shared/modules/grid/filters/time-filter/time-filter-selector.interfaces';
import { AgGridSort } from '@uigy/shared/components/ui-grid/models/ui-grid';
import { GridUtils } from '@uigy/shared/components/ui-grid/grid-utils';
import { TextListFilterComponent } from '@uigy/shared/modules/grid/filters/text-list-filter/text-list-filter.component';
import { TextListFilterParams } from '@uigy/shared/modules/grid/filters/text-list-filter/text-list-filter.interfaces';

@Injectable({
  providedIn: 'root'
})
export class SensorsGridSettingsService {
  private readonly sensorsTexts = this.languageService.texts.settings.system_config.sensors;
  private readonly texts = this.languageService.texts;
  private gridFormatterService = inject(GridFormatterService);

  constructor(private languageService: LanguageService,
              private sensorsService:SensorsService,
              private sensorsDataHelperService: SensorsDataHelperService
  ) {}

  public static readonly defaultSort: AgGridSort[] = [
    {
      sort: 'desc',
      colId: IcpSensorField.StatusTs,
    },
  ];

  private resolveStateIconWithText(params: ICellRendererParams): Pick<StateIndicatorCellRendererParams, 'text' | 'value'> {
    switch (params.data.status) {
      case SensorStatus.ConnectedUnauthenticated:
        return {
          value: StateIndicatorType.Connected,
          text: params.value
        };
      case SensorStatus.Connected:
        return {
          value: StateIndicatorType.Connected,
          text: params.value
        };
      case SensorStatus.NoTunnel:
        return {
          value: StateIndicatorType.Disconnected,
          text: params.value
        };
      case SensorStatus.Pending:
        return {
          value: StateIndicatorType.Pending,
          text: params.value
        };
      case SensorStatus.Paused:
        return {
          value: StateIndicatorType.Paused,
          text: params.value
        };
      case SensorStatus.Updating:
        return {
          value: StateIndicatorType.Running,
          text: params.value
        };
      default:
        return {
          value: StateIndicatorType.Disconnected,
          text: params.value
        };
    }
  }
  private resolveStatusText(params: ValueGetterParams): string {
    return this.gridFormatterService.sensorStatusFormatter(params.data?.status);
  }

  private resolveActiveQueriesText(params: ValueGetterParams): string {
    return this.activeQueriesStatusFormatter(params.data?.active);
  }

  private activeQueriesStatusFormatter(value: SensorActiveStatus | null): string {
    switch (value) {
      case SensorActiveStatus.Enabled:
        return this.texts.general.enabled;
      case SensorActiveStatus.Disabled:
        return this.texts.general.disabled;
      default:
        return this.texts.general.not_available;
    }
  }

  private resolveVersionText(version: string, stockdogUpdateExists: boolean, systemUpdatesExist: boolean): string {
    if(stockdogUpdateExists) {
      version += ` (${this.sensorsTexts.update_sensor.stockdog_update_exist})`;
    } else if(systemUpdatesExist) {
      version += ` (${this.sensorsTexts.update_sensor.system_update_exist})`;
    }

    return version;
  }

  /*  COLUMN DEFS  */
  private readonly IP: ColDef = {
    headerName: this.sensorsTexts.column_defs.ip,
    field: 'ip',
    width: 180,
    enableRowGroup: false,
  };

  private readonly STATUS: ColDef = {
    headerName: this.sensorsTexts.column_defs.status,
    field: 'status',
    minWidth: 200,
    cellRenderer: StateIndicatorCellRendererComponent,
    valueGetter: this.resolveStatusText.bind(this),
    cellRendererParams: this.resolveStateIconWithText.bind(this),
    filterParams: {
      valueFormatter: this.gridFormatterService.sensorStatusFormatter.bind(this)
    }
  };

  private readonly ACTIVE_QUERIES: ColDef = {
    headerName: this.sensorsTexts.column_defs.active_queries,
    field: 'active',
    minWidth: 144,
    valueGetter: this.resolveActiveQueriesText.bind(this),
    filterParams: {
      valueFormatter: this.activeQueriesStatusFormatter.bind(this)
    }
  };

  private readonly ACTIVE_QUERY_NETWORKS: ColDef = {
    headerName: this.sensorsTexts.column_defs.active_query_networks,
    field: 'activeNetworks',
    minWidth: 180,
    enableRowGroup: false,
    valueFormatter: params => params.value?.join(DelimitersEnum.pipe),
    filter: false
  };

  private readonly NAME: ColDef = {
    headerName: this.sensorsTexts.column_defs.name,
    field: 'name',
    minWidth: 172,
    enableRowGroup: false,
  };

  private readonly LAST_UPDATE: ColDef = {
    headerName: this.sensorsTexts.column_defs.last_update,
    field: 'statusTs',
    minWidth: 220,
    filter: TimeFilterSelectorComponent,
    enableRowGroup: false,
    comparator: Utils.sortByDate,
    valueGetter: (params: ValueGetterParams) => params.data?.statusTs ? DateTimeService.utcToLocal(params.data.statusTs) : '',
    valueFormatter: params => params.value ? DateTimeService.utcToLocalFormatted(params.value, DatesFormatsEnum.fullWithDot) : '',
    sort: 'desc',
    filterParams: {
      componentMap: createFilterComponentMap(),
    }
  };

  private readonly SENSOR_ID: ColDef = {
    headerName: this.sensorsTexts.column_defs.sensor_identifier,
    field: 'id',
    hide: true,
    minWidth: 144,
    enableRowGroup: false,
    cellRenderer: 'copyTextRendererComponent',
    filter: TextListFilterComponent,
    filterParams: {
      title: this.sensorsTexts.column_defs.sensor_identifier,
      validationType: 'uuid',
    } as TextListFilterParams,
  };

  private readonly VERSION: ColDef = {
    headerName: this.sensorsTexts.column_defs.version,
    field: 'version',
    minWidth: 144,
    cellClass: (params) => this.sensorsService.isUpdateAvailable(params.data) ? 'g-red' : '',
    valueFormatter: params => params.data ? this.resolveVersionText(params.data.version, params.data.stockdogUpdateExists, params.data.systemUpdatesExist) : params.value,
    filterParams: {
      valueFormatter: (value: string) => value === '' ? this.languageService.texts.general.key_words.blanks : value,
    },
  };

  private readonly PLATFORM_TYPE: ColDef = {
    headerName: this.sensorsTexts.column_defs.platform_type,
    field: 'osVersion',
    minWidth: 180,
    enableRowGroup: false,
    valueGetter: params => params.data?.osVersion ?? '',
    valueFormatter: (params: ValueFormatterParams) => params.data?.osVersion ? this.languageService.getTranslationTyped(this.sensorsDataHelperService.resolvePlatformName(params.data?.osVersion)) : '',
    filterParams: {
      valueFormatter: (platform: CoreOsVersion) => {
        const platformName = this.sensorsDataHelperService.resolvePlatformName(platform);
        return platformName
          ? this.languageService.getTranslationTyped(platformName)
          : this.languageService.texts.general.key_words.blanks;
      }
    }
  };

  private readonly THROUGHPUT: ColDef = {
    headerName: this.sensorsTexts.column_defs.throughput,
    field: 'speed',
    minWidth: 144,
    enableRowGroup: false,
    suppressMenu: true,
    filter: false,
    valueFormatter: params => {
      if (params.value === null) {
        return new FileSizesPipe().transform(0, 2, true);
      }
      // format bytes (returned from server) to bits
      return new FileSizesPipe().transform(params.value * 8, 2, true);
    }
  };

  private readonly NETWORK_AREA: ColDef = {
    headerName: this.sensorsTexts.column_defs.network_area,
    field: 'networkArea',
    minWidth: 180,
    valueGetter: params => params.data?.networkArea?.name ?? '',
  };

  public get ColumnDefs() {
    return [
      this.SENSOR_ID,
      this.IP,
      this.STATUS,
      this.ACTIVE_QUERIES,
      this.ACTIVE_QUERY_NETWORKS,
      this.NAME,
      this.NETWORK_AREA,
      this.LAST_UPDATE,
      this.VERSION,
      this.PLATFORM_TYPE,
      this.THROUGHPUT,
    ];
  }

}
